import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Structured browser geolocation hook for opt-in Delivery checkout.
 *
 * The hook never requests the customer's location until the caller invokes
 * `request()`. Browser geolocation is a convenience input for the Address
 * resolver only; it must never be used to bypass backend verification,
 * coverage, or token issuance.
 */
export type GeolocationStatus =
  | "idle"
  | "unsupported"
  | "requesting"
  | "granted"
  | "denied"
  | "unavailable"
  | "timeout";

export type GeolocationError =
  | { code: 1; message: string }
  | { code: 2; message: string }
  | { code: 3; message: string };

export type UseGeolocationResult = {
  status: GeolocationStatus;
  coords: { lat: number; lng: number; accuracy: number } | null;
  error: GeolocationError | null;
  request: () => void;
  reset: () => void;
};

function decodeGeolocationError(
  code: number,
  message: string,
): { error: GeolocationError; status: GeolocationStatus } {
  if (code === 1) {
    return { error: { code, message }, status: "denied" };
  }
  if (code === 3) {
    return { error: { code, message }, status: "timeout" };
  }
  return { error: { code: 2, message }, status: "unavailable" };
}

export function useGeolocation(): UseGeolocationResult {
  const [status, setStatus] = useState<GeolocationStatus>(() =>
    typeof navigator === "undefined" || !("geolocation" in navigator) ? "unsupported" : "idle",
  );
  const [coords, setCoords] = useState<UseGeolocationResult["coords"]>(null);
  const [error, setError] = useState<GeolocationError | null>(null);
  const watchIdRef = useRef<number | null>(null);

  const reset = useCallback(() => {
    if (watchIdRef.current != null && typeof navigator !== "undefined") {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setStatus(
      typeof navigator === "undefined" || !("geolocation" in navigator) ? "unsupported" : "idle",
    );
    setCoords(null);
    setError(null);
  }, []);

  const request = useCallback(() => {
    if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
      setStatus("unsupported");
      return;
    }
    setStatus("requesting");
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCoords({
          accuracy: position.coords.accuracy,
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setStatus("granted");
      },
      (err) => {
        const decoded = decodeGeolocationError(err.code, err.message);
        setError(decoded.error);
        setStatus(decoded.status);
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10_000 },
    );
  }, []);

  useEffect(() => {
    return () => {
      if (watchIdRef.current != null && typeof navigator !== "undefined") {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, []);

  return { coords, error, request, reset, status };
}
